import { loadVocabularyCatalog } from './vocabularyV2';
import { loadGrammarSections, loadGrammarNativeOverlay } from './grammarV2';
import { loadStories, loadStoriesNativeOverlay } from './storiesV2';
import { fetchTopicLessonsData } from './dataApi';

// Manifest-level search across vocabulary topics, grammar chapters, stories,
// and quick-sheet sections for the current native/target language pair.
// Mirrors mobile's lib/services/search_service.dart: one in-memory index per
// language pair (cached), built from the same four content sources, filtered
// by a case-insensitive substring match on label/nativeLabel. Titles/labels
// only — not full-text word search (see WordLookupService + bookmarks for
// that on mobile; this web port has no equivalent yet).
//
// SearchResult shape: { type: 'vocab'|'grammar'|'story'|'quicksheet', label,
// nativeLabel?, icon? } — `type` matches the Dart source's type strings
// exactly (used for icons/routing in SearchPage.jsx).

const cache = new Map();

// title/note/columns/rows-style fields across this codebase's data are
// authored as { txt, translate, translationHint } — same convention
// QuickSheetPage.jsx unwraps quick-sheet section titles with.
function unwrapText(value) {
  return value && typeof value === 'object' && 'txt' in value ? value.txt ?? '' : value;
}

async function buildIndex(nativeCode, targetCode) {
  const langPair = `${nativeCode}_${targetCode}`;
  if (cache.has(langPair)) return cache.get(langPair);

  const results = [];

  // Vocabulary topics (thematic + word-class catalogs) — labels are already
  // in the target language, no native overlay to resolve.
  try {
    const catalog = await loadVocabularyCatalog(targetCode);
    for (const list of [catalog?.thematic, catalog?.wordclass]) {
      for (const topic of list || []) {
        if (topic && typeof topic === 'object') {
          results.push({
            type: 'vocab',
            label: topic.label != null ? String(topic.label) : '',
            icon: topic.icon != null ? String(topic.icon) : undefined,
          });
        }
      }
    }
  } catch {
    // No vocabulary content for this target language yet — skip rather than
    // failing the whole index.
  }

  // Grammar chapters — id/label list assembled from the target language's
  // lesson sections, with an optional native-language overlay (id -> label)
  // when the reader's native language differs from the target.
  try {
    const targetDoc = await loadGrammarSections(targetCode);
    const chapters = targetDoc?.sections || [];
    let nativeLabelById = {};
    if (nativeCode !== targetCode) {
      const nativeOverlay = await loadGrammarNativeOverlay(targetCode, nativeCode);
      nativeLabelById = Object.fromEntries(
        (nativeOverlay?.sections || [])
          .filter((section) => section && section.id != null && section.label != null)
          .map((section) => [String(section.id), String(section.label)]),
      );
    }
    for (const chapter of chapters) {
      if (chapter && typeof chapter === 'object') {
        results.push({
          type: 'grammar',
          label: chapter.label != null ? String(chapter.label) : '',
          nativeLabel: nativeLabelById[String(chapter.id)],
        });
      }
    }
  } catch {
    // No grammar content for this target language yet.
  }

  // Stories — title/emoji from the target language, with an optional
  // native-language title overlay keyed by story id.
  try {
    const targetDoc = await loadStories(targetCode);
    const targetStories = targetDoc?.stories || [];
    let nativeTitleById = {};
    if (nativeCode !== targetCode) {
      const nativeDoc = await loadStoriesNativeOverlay(targetCode, nativeCode);
      nativeTitleById = Object.fromEntries(
        (nativeDoc?.stories || [])
          .filter((story) => story && story.id != null)
          .map((story) => [String(story.id), story.title != null ? String(story.title) : '']),
      );
    }
    for (const story of targetStories) {
      if (story && typeof story === 'object') {
        results.push({
          type: 'story',
          label: story.title != null ? String(story.title) : '',
          nativeLabel: nativeTitleById[String(story.id)],
          icon: story.emoji != null ? String(story.emoji) : undefined,
        });
      }
    }
  } catch {
    // No stories for this target language yet.
  }

  // Quick-sheet section titles — no native overlay (quick-sheet has none).
  try {
    const sheet = await fetchTopicLessonsData(`${targetCode}/quick-sheet.json`);
    for (const section of sheet?.sections || []) {
      if (!section || typeof section !== 'object') continue;
      const label = unwrapText(section.title);
      if (label) {
        results.push({ type: 'quicksheet', label: String(label) });
      }
    }
  } catch {
    // No quick-sheet for this target language yet.
  }

  cache.set(langPair, results);
  return results;
}

function matchesQuery(result, query) {
  const q = query.toLowerCase();
  return (
    (result.label || '').toLowerCase().includes(q) ||
    (result.nativeLabel || '').toLowerCase().includes(q)
  );
}

async function search(nativeCode, targetCode, query) {
  const q = (query || '').trim();
  if (!q) return [];
  const all = await buildIndex(nativeCode, targetCode);
  return all.filter((result) => matchesQuery(result, q));
}

function clearSearchCache() {
  cache.clear();
}

export const SearchService = { search, clearSearchCache };
